import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useInView } from "framer-motion"

const GLYPHS = "!<>-_\\/#$%&=+*?@[]{}~"
const randomGlyph = () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? "*"

// Glitch text effect - scrambles on scroll into view
export function GlitchText({ 
  children, 
  delay = 0,
  className = "",
}: { 
  children: string
  delay?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-50px" })
  const [display, setDisplay] = useState(children)
  const [isGlitching, setIsGlitching] = useState(false)

  useEffect(() => {
    if (!isInView) return

    const timeout = setTimeout(() => {
      setIsGlitching(true)
      const chars = children.split("")
      let iteration = 0
      const maxIterations = 8

      const interval = setInterval(() => {
        setDisplay(
          chars.map((char, i) => {
            if (char === " ") return " "
            if (iteration > i * 0.5) return char
            return randomGlyph()
          }).join("")
        )
        iteration += 1
        if (iteration >= chars.length + maxIterations) {
          clearInterval(interval)
          setDisplay(children)
          setIsGlitching(false)
        }
      }, 40)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timeout)
  }, [isInView, children, delay])

  return (
    <span 
      ref={ref} 
      className={`glitch-text ${isGlitching ? "glitching" : ""} ${className}`}
      data-text={children}
    >
      {display}
    </span>
  )
}

// Terminal typing effect with cursor
export function TerminalText({ 
  text, 
  delay = 0,
  speed = 30,
  showCursor = true,
}: { 
  text: string
  delay?: number
  speed?: number
  showCursor?: boolean
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-30px" })
  const [displayed, setDisplayed] = useState("")
  const [cursorVisible, setCursorVisible] = useState(true)

  useEffect(() => {
    if (!isInView) return

    const startTimeout = setTimeout(() => {
      let i = 0
      const interval = setInterval(() => {
        if (i < text.length) {
          setDisplayed(text.slice(0, i + 1))
          i++
        } else {
          clearInterval(interval)
        }
      }, speed)
      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(startTimeout)
  }, [isInView, text, delay, speed])

  useEffect(() => {
    if (!showCursor) return
    const interval = setInterval(() => {
      setCursorVisible(v => !v)
    }, 530)
    return () => clearInterval(interval)
  }, [showCursor])

  return (
    <span ref={ref} className="terminal-text">
      {displayed}
      {showCursor && (
        <span 
          className="terminal-cursor"
          style={{ 
            opacity: cursorVisible ? 1 : 0,
            color: "var(--accent-text)",
          }}
        >
          █
        </span>
      )}
    </span>
  )
}

// ASCII art background animation
const ASCII_FRAMES = [
  `┌─────────────────┐
│  ▓▓ INVOICING ▓▓ │
│  ████████████████ │
│  ░░░░░░░░░░░░░░░░ │
└─────────────────┘`,
  `┌─────────────────┐
│  ░░ INVOICING ░░ │
│  ░░░░░░░░░░░░░░░░ │
│  ████████████████ │
└─────────────────┘`,
]

export function ASCIIBackground({ className = "", style = {} }: { className?: string; style?: React.CSSProperties }) {
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setFrame(f => (f + 1) % ASCII_FRAMES.length)
    }, 800)
    return () => clearInterval(interval)
  }, [])

  return (
    <pre
      className={`ascii-background ${className}`}
      style={{
        position: "absolute",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: "10px",
        color: "var(--accent-text)",
        opacity: 0.08,
        pointerEvents: "none",
        lineHeight: 1.2,
        ...style,
      }}
    >
      {ASCII_FRAMES[frame]}
    </pre>
  )
}

// Animated border box with glowing corners
export function AnimatedBorderBox({ 
  children,
  className = "",
  style = {},
}: { 
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <div 
      className={`animated-border-box ${className}`}
      style={{
        position: "relative",
        ...style,
      }}
    >
      <span className="corner top-left">┌</span>
      <span className="corner top-right">┐</span>
      <span className="corner bottom-left">└</span>
      <span className="corner bottom-right">┘</span>
      {children}
    </div>
  )
}

// Scan lines overlay (subtle CRT effect)
export function ScanLines() {
  return (
    <div
      className="scan-lines"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.02) 0px, rgba(0,0,0,0.02) 1px, transparent 1px, transparent 2px)",
        zIndex: 9999,
      }}
    />
  )
}

// Glyph scramble hook (matching Home.tsx pattern)
export function useGlyphScramble(text: string, options?: { scrambleInterval?: number }) {
  const { scrambleInterval = 38 } = options ?? {}
  const characters = useMemo(() => text.split(""), [text])
  const [display, setDisplay] = useState<string[]>(() => characters.map(c => c === " " ? " " : c))
  const intervalsRef = useRef<Array<number | null>>([])

  const clearTimers = useCallback(() => {
    intervalsRef.current.forEach(id => id !== null && window.clearInterval(id))
    intervalsRef.current = []
  }, [])

  const setCharacter = useCallback((index: number, value: string) => {
    setDisplay(prev => { const next = [...prev]; next[index] = value; return next })
  }, [])

  const startLoop = useCallback(() => {
    clearTimers()
    intervalsRef.current = characters.map(() => null)
    characters.forEach((char, index) => {
      if (char === " ") { setCharacter(index, " "); return }
      intervalsRef.current[index] = window.setInterval(() => setCharacter(index, randomGlyph()), scrambleInterval)
    })
  }, [characters, clearTimers, scrambleInterval, setCharacter])

  const stopLoop = useCallback(() => {
    clearTimers()
    setDisplay(characters.map(c => c === " " ? " " : c))
  }, [clearTimers, characters])

  useEffect(() => clearTimers, [clearTimers])

  return { characters, display, startLoop, stopLoop }
}

// Glyph display component
export function GlyphDisplay({ characters, display, prefix }: { characters: string[]; display: string[]; prefix: string }) {
  return (
    <>
      {characters.map((_, i) => (
        <span key={`${prefix}-${i}`} className="glitch-typewriter-char">
          {display[i] || "\u00a0"}
        </span>
      ))}
    </>
  )
}

// Typing indicator dots
export function TypingIndicator() {
  return (
    <span className="typing-indicator">
      <span>.</span>
      <span>.</span>
      <span>.</span>
    </span>
  )
}
